/**
 * Cloud in the app: which server, who is signed in, and the live session of the open cloud
 * project (sync of transactions, assets, presence, offline queue). Kept out of React; the UI
 * subscribes to `CloudState`.
 */
import { assetRefs, contentOf, type History, type Project } from '@elec/domain';
import {
  cloudKey,
  loadLibraryEntry,
  putStoredAsset,
  saveLibraryEntry,
  storedAssets,
  type Platform,
} from '@elec/platform';
import {
  CloudClient,
  CloudError,
  SyncSession,
  type Me,
  type PendingTx,
  type Presence,
  type Role,
  type Team,
} from '@elec/sync';
import type { AssetStore } from './assets.ts';

export const DEFAULT_SERVER = 'https://tablo-app.be';

/** Public pages of the site, in the interface language. */
export const legalUrl = (page: 'privacy' | 'support', locale: string) =>
  `${DEFAULT_SERVER}/${page}/?lang=${locale}`;

/** Server URL: build setting, else the app default (desktop, mobile), else the site serving the web app. */
export function serverUrl(platform: Platform): string | undefined {
  const env =
    (import.meta.env.VITE_API_URL as string | undefined) ||
    (import.meta.env.VITE_AI_URL as string | undefined);
  if (env) return env;
  if (platform.kind !== 'web') return DEFAULT_SERVER;
  if (typeof location !== 'undefined' && location.protocol.startsWith('http')) return location.origin;
  return undefined;
}

export interface LiveState {
  projectId: string;
  teamId: string;
  role: Role;
  online: boolean;
  pending: number;
  presence: Presence[];
  /** Last change rejected because someone else changed the same thing. */
  rejected: number;
  /** Opened from the copy on this device (no network when opening). */
  fromCache: boolean;
  /** Photos and plans not yet uploaded. */
  pendingFiles: number;
}

export interface CloudState {
  /** The server answered /api/health. */
  available: boolean;
  ai: boolean;
  /** The server can turn speech into text (assistant dictation). */
  stt: boolean;
  /** The server can send email (password reset). */
  mail: boolean;
  me: Me | null;
  team: Team | null;
  live: LiveState | null;
  /** Signed in, but the server could not be reached: cloud projects open from this device. */
  offline: boolean;
}

/** No answer from the server (network down, server restarting): not a refusal. */
export const isNetworkError = (e: unknown) => !(e instanceof CloudError) || e.status >= 500;

const PENDING_KEY = (id: string) => `cloud.pending.${id}`;

const CURSOR_COLORS = ['#e8590c', '#1c7ed6', '#2f9e44', '#ae3ec9', '#f08c00', '#0c8599', '#d6336c'];
/** A colour per client, the same on every screen. */
export function cursorColor(clientId: string): string {
  let h = 0;
  for (let i = 0; i < clientId.length; i++) h = (h * 31 + clientId.charCodeAt(i)) >>> 0;
  return CURSOR_COLORS[h % CURSOR_COLORS.length]!;
}

export class CloudManager {
  readonly client: CloudClient | null;
  state: CloudState = {
    available: false,
    ai: false,
    stt: false,
    mail: false,
    me: null,
    team: null,
    live: null,
    offline: false,
  };
  private listeners = new Set<() => void>();
  private session: SyncSession | null = null;
  private stopStream: (() => void) | null = null;
  private stopOnline: (() => void) | null = null;
  private unsubscribeHistory: (() => void) | null = null;
  private readonly platform: Platform;
  /** Called with the rejected transaction's label, to tell the user. */
  onRejected: (label: string) => void = () => undefined;

  constructor(platform: Platform) {
    this.platform = platform;
    const url = serverUrl(platform);
    this.client = url ? new CloudClient(url, platform.getPref('cloud.token')) : null;
  }

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  // ---------- real-time cursors ----------
  // Kept out of the React state: they move many times a second; the canvas listens and redraws.
  private cursors = new Map<string, { sheetId: string; x: number; y: number; at: number }>();
  private cursorListeners = new Set<() => void>();
  private cursorTimer: ReturnType<typeof setTimeout> | null = null;
  private cursorNext: { sheetId: string | null; x: number; y: number } | null = null;
  private cursorSentAt = 0;
  private notifyCursors() {
    for (const f of this.cursorListeners) f();
  }
  onCursors(fn: () => void): () => void {
    this.cursorListeners.add(fn);
    return () => this.cursorListeners.delete(fn);
  }
  /** The other people's pointers on a sheet, with their name and colour (seen in the last 30 s). */
  peerCursors(
    sheetId: string,
  ): Array<{ clientId: string; name: string; color: string; x: number; y: number }> {
    const live = this.state.live;
    if (!live) return [];
    const now = Date.now();
    return [...this.cursors]
      .filter(([, c]) => c.sheetId === sheetId && now - c.at < 30_000)
      .map(([clientId, c]) => ({
        clientId,
        name: live.presence.find((x) => x.clientId === clientId)?.name ?? '',
        color: cursorColor(clientId),
        x: c.x,
        y: c.y,
      }));
  }
  /**
   * Where this user's pointer is (null: left the drawing). Sent 10 times a second at most, and
   * only when someone else has the project open.
   */
  moveCursor(sheetId: string | null, x = 0, y = 0) {
    const live = this.state.live;
    if (!live || !live.online || live.presence.length < 2 || !this.session || !this.client) return;
    this.cursorNext = { sheetId, x: Math.round(x * 10) / 10, y: Math.round(y * 10) / 10 };
    if (this.cursorTimer) return;
    const send = () => {
      this.cursorTimer = null;
      const c = this.cursorNext;
      const s = this.session;
      if (!c || !s || !this.state.live) return;
      this.cursorNext = null;
      this.cursorSentAt = Date.now();
      void this.client!.sendCursor(this.state.live.projectId, { clientId: s.clientId, ...c }).catch(
        () => undefined,
      );
    };
    const wait = Math.max(0, 100 - (Date.now() - this.cursorSentAt));
    this.cursorTimer = setTimeout(send, wait);
  }
  private set(patch: Partial<CloudState>) {
    this.state = { ...this.state, ...patch };
    for (const fn of this.listeners) fn();
  }
  private setLive(patch: Partial<LiveState>) {
    if (this.state.live) this.set({ live: { ...this.state.live, ...patch } });
  }

  /** Check the server and the saved session. Offline, the last known account is kept. */
  async init(): Promise<void> {
    if (!this.client) return;
    try {
      const res = await fetch(`${this.client.baseUrl}/api/health`);
      const h = (await res.json()) as { ai?: boolean; stt?: boolean; cloud?: boolean; mail?: boolean };
      this.set({ available: !!h.cloud, ai: !!h.ai, stt: !!h.stt, mail: !!h.mail, offline: false });
    } catch {
      this.goOffline();
      return;
    }
    if (this.state.available && this.client.token) {
      try {
        await this.signedIn(await this.client.me());
      } catch (e) {
        if (isNetworkError(e)) return this.goOffline();
        this.client.token = null;
        this.platform.setPref('cloud.token', '');
        this.platform.setPref('cloud.me', '');
      }
    }
  }

  private goOffline() {
    if (!this.client?.token) return;
    try {
      const me = JSON.parse(this.platform.getPref('cloud.me') || 'null') as Me | null;
      if (!me) return;
      const saved = this.platform.getPref('cloud.team');
      this.set({ me, team: me.teams.find((t) => t.id === saved) ?? me.teams[0] ?? null, offline: true });
    } catch {
      /* no saved account */
    }
  }

  private async signedIn(me: Me) {
    this.platform.setPref('cloud.token', this.client!.token ?? '');
    this.platform.setPref('cloud.me', JSON.stringify(me));
    const saved = this.platform.getPref('cloud.team');
    this.set({ me, team: me.teams.find((t) => t.id === saved) ?? me.teams[0] ?? null });
  }
  async login(email: string, password: string) {
    await this.signedIn(await this.client!.login(email, password, this.platform.kind));
  }
  /** 'pending': the account works once its address is confirmed (emailed link). */
  async register(
    email: string,
    password: string,
    name: string,
    locale: 'fr' | 'en' | 'nl' | 'de',
  ): Promise<'signed-in' | 'pending'> {
    const r = await this.client!.register(email, password, name, locale);
    if ('pending' in r) return 'pending';
    await this.signedIn(r);
    return 'signed-in';
  }
  /** Confirm the address from the emailed link; signs in here. */
  async confirmEmail(token: string) {
    await this.signedIn(await this.client!.confirmEmail(token, this.platform.kind));
  }
  resendConfirmation(email: string, locale: 'fr' | 'en' | 'nl' | 'de') {
    return this.client!.resendConfirmation(email, locale);
  }
  openUrl(url: string) {
    return this.platform.openUrl(url);
  }
  forgotPassword(email: string, locale: 'fr' | 'en' | 'nl' | 'de') {
    return this.client!.forgotPassword(email, locale);
  }
  /** New password from the emailed link; signs in here. */
  async resetPassword(token: string, password: string, locale: 'fr' | 'en' | 'nl' | 'de') {
    await this.signedIn(await this.client!.resetPassword(token, password, locale, this.platform.kind));
  }
  async logout() {
    this.leave();
    await this.client?.logout();
    this.platform.setPref('cloud.token', '');
    this.platform.setPref('cloud.me', '');
    this.set({ me: null, team: null, offline: false });
  }
  /** Delete the account on the server. Projects kept on this device stay on it. */
  async deleteAccount(password: string, locale: 'fr' | 'en' | 'nl' | 'de') {
    await this.client!.deleteAccount(password, locale);
    this.leave();
    this.platform.setPref('cloud.token', '');
    this.platform.setPref('cloud.me', '');
    this.platform.setPref('cloud.team', '');
    this.set({ me: null, team: null, offline: false });
  }
  async refreshMe() {
    if (this.client?.token) await this.signedIn(await this.client.me());
  }
  selectTeam(team: Team) {
    this.platform.setPref('cloud.team', team.id);
    this.set({ team });
  }

  /** Upload the current project as a new cloud project (with its plan files). Returns its id. */
  async upload(
    project: Project,
    assets: Record<string, Uint8Array>,
    mimes: Record<string, string>,
  ): Promise<string> {
    const team = this.state.team!;
    const { id } = await this.client!.createProject(team.id, project);
    for (const [aid, bytes] of Object.entries(assets))
      await this.client!.putAsset(id, aid, bytes, mimes[aid] ?? 'application/octet-stream');
    return id;
  }

  /**
   * Open a cloud project live: the History is reset to it and from then on its transactions
   * are synced. Without network, it opens from the copy kept on this device and syncs once the
   * connection is back. Returns the project loaded.
   */
  async open(projectId: string, history: History, assets: AssetStore): Promise<Project> {
    this.leave();
    const c = this.client!;
    const key = cloudKey(projectId);
    let base: { project: Project; seq: number; role: Role; teamId: string };
    let fromCache = false;
    try {
      base = await c.openProject(projectId);
    } catch (e) {
      const cached = isNetworkError(e) ? await loadLibraryEntry(key) : null;
      if (!cached?.cloud) throw e;
      base = {
        project: cached.project,
        seq: cached.cloud.seq,
        role: cached.cloud.role,
        teamId: cached.cloud.teamId,
      };
      fromCache = true;
    }
    const { project, seq, role, teamId } = base;
    const pending = ((): PendingTx[] => {
      try {
        return JSON.parse(this.platform.getPref(PENDING_KEY(projectId)) || '[]') as PendingTx[];
      } catch {
        return [];
      }
    })();
    // Files: the copies on this device first, then the server's.
    const stored = await storedAssets(key);
    const files: Record<string, Uint8Array> = {};
    this.uploaded.clear();
    for (const [aid, a] of Object.entries(stored)) {
      files[aid] = a.bytes;
      if (a.uploaded) this.uploaded.add(aid);
    }
    if (!fromCache)
      for (const aid of await c.listAssets(projectId).catch(() => [] as string[])) {
        this.uploaded.add(aid);
        if (files[aid]) continue;
        try {
          const a = await c.asset(projectId, aid);
          files[aid] = a.bytes;
          void putStoredAsset(key, aid, { bytes: a.bytes, mime: a.type, uploaded: true });
        } catch {
          /* missing file: the plan shows without background */
        }
      }
    await assets.replaceAll(files, project);
    let saveTimer: ReturnType<typeof setTimeout> | null = null;
    const keepCopy = (p: Project, s: number) => {
      if (saveTimer) clearTimeout(saveTimer);
      saveTimer = setTimeout(
        () =>
          void saveLibraryEntry({
            key,
            kind: 'cloud',
            title: p.meta.title,
            updatedAt: Date.now(),
            project: p,
            cloud: { projectId, teamId, seq: s, role },
          }),
        500,
      );
    };
    keepCopy(project, seq);
    const session = new SyncSession({
      project,
      seq,
      pending,
      send: (r) => c.sendTx(projectId, r),
      onView: (p) => {
        history.replace(p);
        void this.fetchMissingAssets(p, assets);
      },
      onConfirmed: keepCopy,
      onRejected: (tx) => {
        this.setLive({ rejected: (this.state.live?.rejected ?? 0) + 1 });
        this.onRejected(tx.label);
      },
      onNetwork: (ok) => this.setLive({ online: ok }),
      onPending: (list) => {
        this.platform.setPref(PENDING_KEY(projectId), JSON.stringify(list));
        this.setLive({ pending: list.length });
      },
    });
    this.session = session;
    this.currentProject = () => history.project;
    history.reset(session.view());
    history.readOnly = role === 'viewer';
    this.unsubscribeHistory = history.subscribeTransactions((tx) => {
      session.local(tx);
      void this.pushNewAssets(projectId, history.project, assets);
    });
    this.set({
      live: {
        projectId,
        teamId,
        role,
        online: false,
        pending: session.pending.length,
        presence: [],
        rejected: 0,
        fromCache,
        pendingFiles: 0,
      },
    });
    void this.pushNewAssets(projectId, history.project, assets);
    // Back online (leaving a dead zone): send the queue and the photos now, not after the backoff.
    const onOnline = () => {
      session.setOnline(true);
      void this.pushNewAssets(projectId, history.project, assets);
    };
    window.addEventListener('online', onOnline);
    this.stopOnline = () => window.removeEventListener('online', onOnline);
    this.stopStream = c.connect(projectId, {
      clientId: session.clientId,
      since: () => session.seq,
      onEvent: (ev) => {
        if (ev.type === 'presence') {
          this.setLive({ presence: ev.clients });
          // Cursors of clients that left go with them.
          const here = new Set(ev.clients.map((x) => x.clientId));
          for (const id of [...this.cursors.keys()]) if (!here.has(id)) this.cursors.delete(id);
          this.notifyCursors();
        } else if (ev.type === 'cursor') {
          if (ev.sheetId === null) this.cursors.delete(ev.clientId);
          else this.cursors.set(ev.clientId, { sheetId: ev.sheetId, x: ev.x, y: ev.y, at: Date.now() });
          this.notifyCursors();
        } else session.remote(ev);
      },
      onStatus: (online) => {
        session.setOnline(online);
        this.setLive({ online });
        if (online) {
          if (this.state.offline) void this.init();
          void this.pushNewAssets(projectId, history.project, assets);
          void this.fetchMissingAssets(history.project, assets);
        }
      },
    });
    return history.project;
  }

  /** Stop syncing (open a local file, sign out…). */
  leave(history?: History) {
    this.stopOnline?.();
    this.stopOnline = null;
    if (this.uploadRetry) clearTimeout(this.uploadRetry);
    this.uploadRetry = null;
    this.currentProject = null;
    this.stopStream?.();
    this.unsubscribeHistory?.();
    this.stopStream = this.unsubscribeHistory = null;
    this.cursors.clear();
    this.notifyCursors();
    this.session?.dispose();
    this.session = null;
    if (history) history.readOnly = false;
    if (this.state.live) this.set({ live: null });
  }

  /** Files of the open cloud project known to be on the server. */
  private uploaded = new Set<string>();
  private uploading = false;
  /** Asked again while uploading: run once more when done. */
  private uploadAgain = false;
  private uploadRetry: ReturnType<typeof setTimeout> | null = null;
  private uploadRetryMs = 5000;
  /**
   * Keep every file of the project on this device and upload the ones the server does not
   * have yet (a photo taken offline goes up when the connection is back).
   */
  private async pushNewAssets(projectId: string, project: Project, assets: AssetStore): Promise<void> {
    if (this.uploading) {
      this.uploadAgain = true;
      return;
    }
    this.uploading = true;
    if (this.uploadRetry) clearTimeout(this.uploadRetry);
    this.uploadRetry = null;
    const key = cloudKey(projectId);
    let failed = false;
    try {
      const todo = assetRefs(project).filter((r) => !this.uploaded.has(r.id) && assets.bytes.get(r.id));
      this.setLive({ pendingFiles: todo.length });
      for (const r of todo) {
        const b = assets.bytes.get(r.id)!;
        await putStoredAsset(key, r.id, { bytes: b, mime: r.mime, uploaded: false });
        try {
          await this.client!.putAsset(projectId, r.id, b, r.mime);
        } catch {
          failed = true;
          continue; // offline: retried (timer, reconnection, next change)
        }
        this.uploaded.add(r.id);
        await putStoredAsset(key, r.id, { bytes: b, mime: r.mime, uploaded: true });
      }
      const left = assetRefs(project).filter(
        (r) => !this.uploaded.has(r.id) && assets.bytes.get(r.id),
      ).length;
      if (this.state.live?.projectId === projectId) this.setLive({ pendingFiles: left });
    } finally {
      this.uploading = false;
    }
    const open = () => this.state.live?.projectId === projectId;
    if (this.uploadAgain && open()) {
      this.uploadAgain = false;
      return this.pushNewAssets(projectId, this.currentProject?.() ?? project, assets);
    }
    this.uploadAgain = false;
    if (failed && open()) {
      this.uploadRetry = setTimeout(() => {
        this.uploadRetry = null;
        if (open()) void this.pushNewAssets(projectId, this.currentProject?.() ?? project, assets);
      }, this.uploadRetryMs);
      this.uploadRetryMs = Math.min(this.uploadRetryMs * 2, 60_000);
    } else this.uploadRetryMs = 5000;
  }
  /** The open project as it is now (set while a cloud project is open). */
  private currentProject: (() => Project) | null = null;
  private async fetchMissingAssets(project: Project, assets: AssetStore) {
    const live = this.state.live;
    if (!live) return;
    const drawn = new Set(Object.values(project.sheets).map((s) => s.background?.assetId));
    for (const r of assetRefs(project)) {
      if (assets.bytes.has(r.id)) continue;
      try {
        const a = await this.client!.asset(live.projectId, r.id);
        this.uploaded.add(r.id);
        await assets.add(r.id, a.bytes, r.mime, drawn.has(r.id));
        void putStoredAsset(cloudKey(live.projectId), r.id, { bytes: a.bytes, mime: r.mime, uploaded: true });
      } catch {
        /* not uploaded yet: retried on the next change */
      }
    }
  }

  /** The project as stored for templates (no revisions). */
  static template(project: Project) {
    return { ...contentOf(project), revisions: {} };
  }
}
